import { axios } from '@/utils/axios'

const AUTO_CLOSE_DELAY = 15000 // 15 seconds
const POLLING_INTERVAL = 2000 // 2 seconds
const MAX_POLLING_DURATION = 30 * 60 * 1000 // 30 minutes - timeout if no completion
const STALE_PENDING_THRESHOLD = 30000 // 30 seconds - if job hasn't been picked up by a worker

export default {
    state: {
        actions: [],
        pollingIntervals: {}, // Store intervals separately to avoid reactivity issues
    },

    mutations: {
        'backgroundActions:add': function (state, action) {
            state.actions.unshift(action)
        },
        'backgroundActions:update': function (state, { id, updates }) {
            const index = state.actions.findIndex((action) => action.id === id)

            if (index !== -1) {
                const oldAction = state.actions[index]
                state.actions[index] = {
                    ...oldAction,
                    ...updates,
                }
            } else {
                console.warn('[BackgroundAction] Mutation update - action not found:', id)
            }
        },
        'backgroundActions:remove': function (state, id) {
            state.actions = state.actions.filter((action) => action.id !== id)
        },
        'backgroundActions:setPollingInterval': function (state, { id, intervalId }) {
            state.pollingIntervals[id] = intervalId
        },
        'backgroundActions:clearPollingInterval': function (state, id) {
            if (state.pollingIntervals[id]) {
                clearInterval(state.pollingIntervals[id])
                delete state.pollingIntervals[id]
            }
        },
    },

    getters: {
        backgroundActions(state) {
            return state.actions
        },
        // Returns only actions that should show in the toast
        visibleBackgroundActions(state) {
            return state.actions.filter((action) => !action.suppressToast)
        },
        getBackgroundActionById: (state) => (id) => {
            return state.actions.find((action) => action.id === id)
        },
    },

    actions: {
        addBackgroundAction({ commit }, updates) {
            const id = Date.now()
            const action = {
                id,
                status: 'in-progress',
                progress: 0,
                progressMessage: '',
                startTime: new Date(),
                endTime: null,
                error: null,
                statusMessage: null,
                // For retry functionality
                retryCallback: null,
                retryParams: null,
                // For navigation on completion
                navigateTo: null,
                resultId: null,
                // Polling config
                pollUrl: null,
                // Toast visibility - can be suppressed when user is on dedicated progress page
                suppressToast: false,
                ...updates,
            }
            commit('backgroundActions:add', action)
            return id
        },
        updateBackgroundAction({ commit }, { id, updates }) {
            commit('backgroundActions:update', { id, updates })
        },

        /**
         * Show the toast for an action (unsuppress it)
         * Called when user navigates away from a dedicated progress page
         */
        showBackgroundActionToast({ commit, getters }, id) {
            const action = getters.getBackgroundActionById(id)
            if (action) {
                commit('backgroundActions:update', { id, updates: { suppressToast: false } })
            } else {
                console.warn('[BackgroundAction] Cannot show toast - action not found:', id)
            }
        },

        /**
         * Start polling for a background action
         * This runs independently of any component lifecycle
         * Includes timeout protection - if no completion after MAX_POLLING_DURATION, fail the action
         */
        startPolling({ commit, dispatch, getters }, { id, pollUrl, onComplete, onFail }) {
            // Clear any existing polling for this action
            commit('backgroundActions:clearPollingInterval', id)

            const pollingStartTime = Date.now()

            const poll = async () => {
                // Use getter to get fresh state
                const action = getters.getBackgroundActionById(id)
                if (!action || action.status !== 'in-progress') {
                    commit('backgroundActions:clearPollingInterval', id)
                    return
                }

                // Check for polling timeout (safety net for unresponsive backend)
                const pollingDuration = Date.now() - pollingStartTime
                if (pollingDuration > MAX_POLLING_DURATION) {
                    console.error(`[BackgroundAction] Polling timeout after ${Math.round(pollingDuration / 1000 / 60)} minutes`)
                    commit('backgroundActions:clearPollingInterval', id)
                    if (onFail) {
                        onFail('Operation timed out. Please try again later.')
                    }
                    return
                }

                try {
                    const response = await axios.get(pollUrl)
                    const statusData = response.data.payload

                    dispatch('updateBackgroundAction', {
                        id,
                        updates: {
                            progress: statusData.progress || 0,
                            progressMessage: statusData.progress_message || 'Processing...',
                            statusMessage: statusData.status_message || null,
                        },
                    })

                    // Detect stale pending jobs (worker likely not running)
                    if (statusData.status === 'pending' && pollingDuration > STALE_PENDING_THRESHOLD) {
                        commit('backgroundActions:clearPollingInterval', id)
                        if (onFail) {
                            onFail('The analysis service isn\'t responding. It may be temporarily down — please try again in a few minutes.')
                        }
                        return
                    }

                    if (statusData.status === 'completed') {
                        commit('backgroundActions:clearPollingInterval', id)
                        if (onComplete) {
                            onComplete(statusData)
                        }
                    } else if (statusData.status === 'failed') {
                        commit('backgroundActions:clearPollingInterval', id)
                        if (onFail) {
                            onFail(statusData.error || 'Operation failed', statusData)
                        }
                    }
                } catch (error) {
                    console.error('[BackgroundAction] Polling error:', error)
                    // Don't stop polling on network errors - retry
                }
            }

            // Start polling
            const intervalId = setInterval(poll, POLLING_INTERVAL)
            commit('backgroundActions:setPollingInterval', { id, intervalId })

            // Poll immediately
            poll()
        },

        stopPolling({ commit }, id) {
            commit('backgroundActions:clearPollingInterval', id)
        },

        completeBackgroundAction({ commit, dispatch }, { id, resultId, navigateTo, navigateToQueryParams }) {
            commit('backgroundActions:clearPollingInterval', id)
            dispatch('updateBackgroundAction', {
                id,
                updates: {
                    status: 'completed',
                    progress: 100,
                    endTime: new Date(),
                    resultId,
                    navigateTo,
                    navigateToQueryParams,
                },
            })

            // Set up auto-close
            setTimeout(() => {
                dispatch('removeBackgroundAction', id)
            }, AUTO_CLOSE_DELAY)
        },

        failBackgroundAction({ commit, dispatch }, { id, error, resultId, navigateTo, navigateToQueryParams  }) {
            commit('backgroundActions:clearPollingInterval', id)
            dispatch('updateBackgroundAction', {
                id,
                updates: {
                    status: 'failed',
                    progress: 0,
                    endTime: new Date(),
                    error: error || 'An error occurred',
                    resultId,
                    navigateTo,
                    navigateToQueryParams,
                },
            })
            // Don't auto-close failed actions - let user dismiss or retry
        },

        removeBackgroundAction({ commit }, id) {
            // Clean up any polling intervals associated with this action
            commit('backgroundActions:clearPollingInterval', id)
            commit('backgroundActions:remove', id)
        },
    },
}
